<template>
  <div class="compact-form">
    <osv-app-alert osv-alert-position="osv-alert-relative" />
    <form @submit.prevent="signUp" v-if="!this.isCreated">
      <alert-message
        :messages="errorMessage"
        type="danger"
        ref="osvAlertMessage"
      />

      <p class="text-grey">
        {{ $t("connexionInformations") }}
      </p>

      <!-- Email -->
      <div class="form-group">
        <b-field
          :label="$t('email')"
          custom-class="required"
          label-position="on-border"
        >
          <b-input @copy.native.prevent
                   @paste.native.prevent
                   required
                   v-model="email"
                   type="email"
          />
        </b-field>
      </div>

      <!-- Confirm email -->
      <div class="form-group">
        <b-field
            :label="$t('confirmEmail')"
            custom-class="required"
            label-position="on-border"
        >
          <b-input @copy.native.prevent
                   @paste.native.prevent
                   required
                   v-model="confirmEmail"
                   type="email"
          />
        </b-field>
      </div>

      <!-- Password -->
      <div class="form-group">
        <b-field
          :label="$t('password')"
          custom-class="required"
          label-position="on-border"
          :message="$t('passwordRules')"
        >
          <b-input
            required
            v-model="password"
            type="password"
            :password-reveal="true"
          />
        </b-field>
      </div>

      <!-- Confirm password -->
      <div class="form-group">
        <b-field
          :label="$t('confirmPassword')"
          custom-class="required"
          label-position="on-border"
        >
          <b-input
            required
            v-model="confirmPassword"
            type="password"
            :password-reveal="true"
          />
        </b-field>
      </div>

      <p class="text-grey mt-5 mb-4">
        {{ $t("myPersonalInformation") }}
      </p>

      <!-- Title -->
      <div class="columns">
        <div class="column">
          <b-radio v-model="title" checked name="title" native-value="">
            {{ $t("titleList.undefined") }}
          </b-radio>
        </div>
        <div class="column">
          <b-radio v-model="title" name="title" native-value="mrs">
            {{ $t("titleList.mrs") }}
          </b-radio>
        </div>
        <div class="column">
          <b-radio v-model="title" name="title" native-value="mr">
            {{ $t("titleList.mr") }}
          </b-radio>
        </div>
      </div>

      <!-- Lastname & Firstname -->
      <div class="columns mb-0">
        <div class="column pb-0">
          <b-field
            :label="$t('lastname')"
            custom-class="required"
            label-position="on-border"
          >
            <b-input required v-model="lastname" />
          </b-field>
        </div>
        <div class="column pb-0">
          <b-field
            :label="$t('firstname')"
            custom-class="required"
            label-position="on-border"
          >
            <b-input required v-model="firstname" />
          </b-field>
        </div>
      </div>

      <!-- Date of birth -->
      <div class="form-group">
        <date-of-birth v-model="dateOfBirth" />
      </div>

      <!-- Phone -->
      <div class="form-group">
        <b-field
            :label="$t('phone')"
            label-position="on-border"
            :custom-class="additionalRequiredFields.includes('phone') ? 'required' : ''"
        >
          <b-input
            v-model="phone"
            type="tel"
            v-cleave="masks.frenchPhone"
            @input.native="onPhoneNumberInput"
            @keydown.native="phoneNumberKeyDown"
            placeholder="06-00-00-00-00"
            maxlength="14"
            :has-counter="false"
            :required="additionalRequiredFields.includes('phone')"
          />
        </b-field>
      </div>

      <!-- Address -->
      <div class="form-group">
        <b-field
            :label="$t('address')"
            label-position="on-border"
            :custom-class="additionalRequiredFields.includes('address') ? 'required' : ''"
        >
          <b-input
              v-model="address"
              :required="additionalRequiredFields.includes('address')"
          />
        </b-field>
      </div>

      <!-- Postal Code & City -->
      <div class="form-group">
        <div class="columns">
          <div class="column">
            <b-field
                :label="$t('postalCode')"
                label-position="on-border"
                :custom-class="additionalRequiredFields.includes('postalCode') ? 'required' : ''"
            >
              <b-input
                  v-model="postalCode"
                  :required="additionalRequiredFields.includes('postalCode')"
              />
            </b-field>
          </div>
          <div class="column">
            <b-field
                :label="$t('city')"
                label-position="on-border"
                :custom-class="additionalRequiredFields.includes('city') ? 'required' : ''"
            >
              <b-input
                  v-model="city"
                  :required="additionalRequiredFields.includes('city')"
              />
            </b-field>
          </div>
        </div>
      </div>

      <!-- Checkbox list -->
      <div
          class="form-group"
          v-if="showNewsletter"
      >
        <b-checkbox v-model="newsletter">
          {{ $t("newsletter") }}
          <cite class="text-grey">{{ $t("newsletterPerMonth") }}</cite>
        </b-checkbox>
      </div>
      <div
          class="form-group"
          v-if="showCommercialOffer"
      >
        <b-checkbox v-model="commercialOffer">
          {{ $t("commercialOffer", { name: this.tenant }) }}
          <cite class="text-grey">{{ $t("promosAndOffers") }}</cite>
        </b-checkbox>
      </div>
      <div class="form-group">
        <b-checkbox v-model="acceptGdpr">
          {{ $t("acceptGdpr") }}
        </b-checkbox>
        <br />
        <a
          :href="this.osvConfig.paths.gdprCustomer"
          v-if="this.osvConfig.paths.gdprCustomer"
          target="_blank"
        >
          {{ $t("gdpr") }}
        </a>
      </div>

      <div class="form-group text-right">
        <a
          v-if="this.osvConfig.paths.signIn"
          :href="this.osvConfig.paths.signIn"
        >
          <b-button
            rounded
            type="is-link is-light"
          >
            {{ $t("IHaveAnAccount") }}
          </b-button>
        </a>
        <b-button
          outlined
          rounded
          type="is-primary"
          native-type="submit"
          :loading="loadingCreated"
        >
          {{ $t("create") }}
        </b-button>
      </div>
    </form>

    <div v-else>
      <alert-message :messages="[$t('accountIsCreated')]" type="info" />
    </div>
  </div>
</template>

<script>
import { HelpersMixin } from "@/mixins/HelpersMixin";
import { ConfigMixin } from "@/mixins/ConfigMixin";
import { LocaleMixin } from "@/mixins/LocaleMixin";
import { PhoneNumberMixin } from "@/mixins/PhoneNumberMixin";
import store from "@/store";
import VueI18n from "vue-i18n";
import cug from "@/services/cug";
import AlertMessage from "../shared/AlertMessage";
import DateOfBirth from "@/components/ui/DateOfBirth";
import OsvAppAlert from "@/components/shared/OsvAppAlert";

export default {
  store,
  name: "SignUpForm",
  mixins: [
    LocaleMixin,
    HelpersMixin,
    ConfigMixin,
    PhoneNumberMixin
  ],
  components: {OsvAppAlert, DateOfBirth, AlertMessage },

  props: {
    additionalRequiredFields: {
      type: Array,
      default: []
    },
    showCommercialOffer: {
      type: Boolean,
      default: true
    },
    showNewsletter: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      tenant: "",

      email: "",
      confirmEmail: "",
      password: "",
      confirmPassword: "",

      title: "",
      lastname: "",
      firstname: "",
      dateOfBirth: null,
      phone: "",
      address: "",
      city: "",
      postalCode: "",

      newsletter: false,
      commercialOffer: false,
      acceptGdpr: false,

      isCreated: false,
      loadingCreated: false,
      errorMessage: []
    };
  },

  methods: {
    signUp: function() {
      this.errorMessage = [];

      if (!this.acceptGdpr) {
        this.errorMessage = [
          this.$t("youMustAcceptGdpr", { name: this.tenant })
        ];
        this.windowTop();
        return false;
      }

      if (this.email !== this.confirmEmail) {
        this.errorMessage = [
          this.$t("emailsAreNotIdentical")
        ];
        this.windowTop();
        return false;
      }

      if (this.loadingCreated) { return false; }
      this.loadingCreated = true;

      cug
        .signUp({
          email: this.email,
          password: this.password,
          password_confirmation: this.confirmPassword,
          title: this.title,
          lastname: this.lastname,
          firstname: this.firstname,
          date_of_birth: this.dateOfBirth,
          phone: this.phone,
          address: this.address,
          city: this.city,
          zipcode: this.postalCode,
          newsletter: this.postalCode,
          commercial_offer: this.commercialOffer,
          accept_gdpr: this.acceptGdpr
        })
        .then(() => {
          this.isCreated = true;
        })
        .catch(err => {
          this.windowTop();
          this.$root.$emit("OsvAlert_ErrorFromApi", err, "customer");
        })
        .finally(() => {
          this.loadingCreated = false;
        });
    }
  },

  beforeMount() {
    cug.whoami().then(rep => {
      this.tenant = rep.data.name;
    });
  },

  // Translate component
  mounted() {
    this.$i18n.locale = this.getLocale();
  },
  i18n: new VueI18n({
    locale: "fr",
    messages: {
      en: {
        connexionInformations: "My login details",
        myPersonalInformation: "My personal information",
        email: "Email",
        confirmEmail: "Confirm email",
        password: "Password",
        confirmPassword: "Confirm password",
        lastname: "Lastname",
        firstname: "Firstname",
        title: "Mister or Madame",
        dateOfBirth: "date of birth",
        datePlaceHolder: "dd/mm/yyyy",
        address: "Address",
        phone: "Phone",
        city: "City",
        postalCode: "Postal code",
        create: "Create my account",
        IHaveAnAccount: "I have an account ?",
        connexionError: "An error occurred during connection",
        accountIsCreated:
          'Your account is created.<br><span class="text-grey">We have sent you a confirmation email to finalize your registration.</span>',
        titleList: {
          undefined: "Undefined",
          mr: "Mister",
          mrs: "Madame"
        },
        newsletter: "I would like to receive the newsletter",
        newsletterPerMonth: "(1 to 2 per month)",
        commercialOffer: "I would like to receive good maps of {name}",
        promosAndOffers: "(Promotions, personalized offers, etc.)",
        acceptGdpr:
          "I have read and agree to the processing of my personal data",
        gdpr: "Data Management Policy",
        youMustAcceptGdpr: "You must accept the {name}'s data management policy",
        passwordRules: "should contain : 8 characters, 1 lowercase, 1 uppercase, 1 digit",
        emailsAreNotIdentical: "The two emails are not identical"
      },
      fr: {
        connexionInformations: "Mes informations de connexion",
        myPersonalInformation: "Mes informations personnelles",
        email: "Email",
        confirmEmail: "Confirmez votre email",
        password: "Mot de passe",
        confirmPassword: "Confirmer le mot de passe",
        lastname: "Nom",
        firstname: "Prénom",
        title: "Monsieur ou Madame",
        dateOfBirth: "Date de naissance",
        datePlaceHolder: "jj/mm/aaaa",
        address: "Adresse",
        phone: "Téléphone",
        city: "Ville",
        postalCode: "Code postal",
        create: "Créer mon compte",
        IHaveAnAccount: "J'ai déjà un compte ?",
        connexionError: "Une erreur s'est produite lors de la connexion",
        accountIsCreated:
          'Votre compte est créé.<br><span class="text-grey">Nous vous avons envoyé un mail de confirmation pour finaliser votre inscription.</span>',
        titleList: {
          undefined: "Indéfini",
          mr: "Monsieur",
          mrs: "Madame"
        },
        newsletter: "Je souhaite recevoir la newsletter",
        newsletterPerMonth: "(1 à 2 par mois)",
        commercialOffer: "Je souhaite recevoir les bons plans de {name}",
        promosAndOffers: "(Promos, offres personnalisées, etc.)",
        acceptGdpr:
          "J'ai lu et j'accepte le traitement de mes données personnelles",
        gdpr: "Politique de gestion des données",
        youMustAcceptGdpr:
          "Vous devez accepter la politique de gestion des données de {name}",
        passwordRules: "doit contenir : 8 caractères, 1 minuscule, 1 majuscule, 1 chiffre",
        emailsAreNotIdentical: "Les deux emails ne sont pas identiques"
      }
    }
  })
};
</script>

<style lang="scss" src="../../assets/styles/buefy/base.scss"></style>
